/**
 * iter307ch — Automations tab for Business Settings.
 *
 * Lets admins register external integrations (n8n, Zoho, Zapier, custom
 * webhooks). Each entry stores:
 *   - inbound credential (header name + value) for systems calling our APIs
 *   - outbound credential (base URL + auth method + token) for when we call them
 *   - allowed_endpoints scope so a single key can be locked to specific routes
 *
 * Credentials are masked in the table; admin must click "Reveal" to see
 * the raw value (logged to `system_automations_audit`).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Textarea } from '../../../components/ui/textarea';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../components/ui/table';
import {
  Plus, Loader2, Eye, EyeOff, Copy, RotateCw, Trash2, Pencil, AlertCircle, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../services/api';

const STATUS_STYLES = {
  active: 'bg-emerald-100 text-emerald-700',
  disabled: 'bg-slate-100 text-slate-600',
};

const _typeLabel = (t) => ({
  n8n: 'n8n', zapier: 'Zapier', zoho: 'Zoho', make: 'Make.com',
  custom_webhook: 'Custom Webhook', custom_api_consumer: 'Custom API Consumer',
}[t] || t);

export const AutomationsTab = () => {
  const [loading, setLoading] = useState(true);
  const [automations, setAutomations] = useState([]);
  const [options, setOptions] = useState({ automation_types: [], auth_methods: [], known_endpoints: [] });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [revealedKey, setRevealedKey] = useState({});  // { [automationId]: { header_value, outbound_token } }

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, optsRes] = await Promise.all([
        api.get('/admin/automations'),
        api.get('/admin/automations/options'),
      ]);
      setAutomations(listRes.data?.automations || []);
      setOptions(optsRes.data || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load automations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleReveal = async (a) => {
    if (revealedKey[a.id]) {
      setRevealedKey((p) => ({ ...p, [a.id]: undefined }));
      return;
    }
    try {
      const { data } = await api.get(`/admin/automations/${a.id}/reveal`);
      setRevealedKey((p) => ({ ...p, [a.id]: data }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reveal failed');
    }
  };

  const handleCopy = (val) => {
    if (!val) return;
    navigator.clipboard.writeText(val);
    toast.success('Copied to clipboard');
  };

  const handleRotate = async (a) => {
    if (!window.confirm(`Rotate key for "${a.name}"? The current key will stop working immediately.`)) return;
    try {
      const { data } = await api.post(`/admin/automations/${a.id}/rotate-key`);
      const newVal = data?.header_value || data?.outbound_token;
      setRevealedKey((p) => ({ ...p, [a.id]: data }));
      toast.success(`Key rotated. New value: ${newVal?.slice(0, 12)}...`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rotate failed');
    }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`Delete automation "${a.name}"? External systems using this key will start receiving 401s immediately.`)) return;
    try {
      await api.delete(`/admin/automations/${a.id}`);
      toast.success('Automation deleted');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const handleToggleStatus = async (a) => {
    const next = a.status === 'active' ? 'disabled' : 'active';
    try {
      await api.patch(`/admin/automations/${a.id}`, { status: next });
      toast.success(`Status: ${next}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">External Automations</h3>
          <p className="text-sm text-slate-500">
            API keys + integration credentials for n8n, Zoho, Zapier and custom webhooks.
            Credentials are masked. Click <Eye className="inline h-3 w-3 mx-0.5" /> to reveal.
          </p>
        </div>
        <Button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="create-automation-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Automation
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
          ) : automations.length === 0 ? (
            <div className="py-12 text-center text-slate-500">
              No automations configured. Click <span className="font-semibold">New Automation</span> to add n8n / Zoho / Zapier.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name / Type</TableHead>
                  <TableHead>Auth</TableHead>
                  <TableHead>Credential</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>Activity (30d)</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {automations.map((a) => {
                  const revealed = revealedKey[a.id];
                  const displayed = revealed
                    ? (revealed.header_value || revealed.outbound_token || '')
                    : (a.header_value_masked || a.outbound_token_masked || '—');
                  return (
                    <TableRow key={a.id} data-testid={`automation-row-${a.id}`}>
                      <TableCell>
                        <div className="font-medium">{a.name}</div>
                        <div className="text-xs text-slate-500">{_typeLabel(a.type)}</div>
                        <Badge className={`text-xs mt-1 ${STATUS_STYLES[a.status] || ''}`}>{a.status}</Badge>
                      </TableCell>
                      <TableCell className="text-xs">
                        {a.header_name && <div><span className="font-mono bg-slate-100 px-1 rounded">{a.header_name}</span></div>}
                        {a.auth_method && <div className="text-slate-500">{a.auth_method}</div>}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 max-w-[300px]">
                          <code className="text-xs bg-slate-50 px-2 py-1 rounded border truncate flex-1">{displayed}</code>
                          <Button size="sm" variant="ghost" onClick={() => handleReveal(a)} title={revealed ? 'Hide' : 'Reveal'} data-testid={`automation-reveal-${a.id}`}>
                            {revealed ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                          </Button>
                          {revealed && (
                            <Button size="sm" variant="ghost" onClick={() => handleCopy(displayed)} title="Copy" data-testid={`automation-copy-${a.id}`}>
                              <Copy className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">
                        {a.allowed_endpoints?.length
                          ? <span title={a.allowed_endpoints.join('\n')}>{a.allowed_endpoints.length} endpoint(s)</span>
                          : <span className="text-slate-400">all</span>}
                      </TableCell>
                      <TableCell className="text-xs">
                        <div>{a.call_count_30d ?? 0} calls</div>
                        {a.last_used_at && (
                          <div className="text-slate-500">last: {String(a.last_used_at).slice(0, 16).replace('T', ' ')}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button size="sm" variant="ghost" onClick={() => handleToggleStatus(a)} title={a.status === 'active' ? 'Disable' : 'Enable'} data-testid={`automation-toggle-${a.id}`}>
                          {a.status === 'active' ? <AlertCircle className="h-3.5 w-3.5 text-amber-600" /> : <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => { setEditing(a); setShowForm(true); }} title="Edit" data-testid={`automation-edit-${a.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleRotate(a)} title="Rotate key" data-testid={`automation-rotate-${a.id}`}>
                          <RotateCw className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(a)} className="text-red-600" title="Delete" data-testid={`automation-delete-${a.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AutomationFormDialog
        open={showForm}
        onOpenChange={(v) => { if (!v) { setShowForm(false); setEditing(null); } }}
        automation={editing}
        options={options}
        onSaved={refresh}
      />
    </div>
  );
};


const AutomationFormDialog = ({ open, onOpenChange, automation, options, onSaved }) => {
  const isEdit = !!automation?.id;
  const [form, setForm] = useState({
    name: '', type: 'n8n', status: 'active', description: '',
    header_name: 'X-API-Key', header_value: '',
    base_url: '', auth_method: 'api_key_header', outbound_token: '',
    allowed_endpoints: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [createdValue, setCreatedValue] = useState(null);

  useEffect(() => {
    if (open) {
      if (automation) {
        setForm({
          name: automation.name || '',
          type: automation.type || 'n8n',
          status: automation.status || 'active',
          description: automation.description || '',
          header_name: automation.header_name || 'X-API-Key',
          header_value: '',  // never prefill (use Reveal in main table)
          base_url: automation.base_url || '',
          auth_method: automation.auth_method || 'api_key_header',
          outbound_token: '',
          allowed_endpoints: automation.allowed_endpoints || [],
        });
      } else {
        setForm({
          name: '', type: 'n8n', status: 'active', description: '',
          header_name: 'X-API-Key', header_value: '',
          base_url: '', auth_method: 'api_key_header', outbound_token: '',
          allowed_endpoints: [],
        });
      }
      setCreatedValue(null);
    }
  }, [open, automation]);

  const toggleEndpoint = (path) => {
    setForm((f) => {
      const has = f.allowed_endpoints.includes(path);
      return { ...f, allowed_endpoints: has ? f.allowed_endpoints.filter((p) => p !== path) : [...f.allowed_endpoints, path] };
    });
  };

  const handleSubmit = async () => {
    if (!form.name) { toast.error('Name is required'); return; }
    setSubmitting(true);
    try {
      const body = {
        name: form.name,
        type: form.type,
        status: form.status,
        description: form.description || null,
        header_name: form.header_name || null,
        header_value: form.header_value || null,
        base_url: form.base_url || null,
        auth_method: form.auth_method || null,
        outbound_token: form.outbound_token || null,
        allowed_endpoints: form.allowed_endpoints,
      };
      if (isEdit) {
        await api.patch(`/admin/automations/${automation.id}`, body);
        toast.success('Automation updated');
        onOpenChange(false);
        onSaved?.();
      } else {
        const { data } = await api.post('/admin/automations', body);
        const raw = data?.automation?.header_value || data?.automation?.outbound_token;
        if (raw) {
          // Show the raw key ONCE so admin can copy it.
          setCreatedValue({ header_name: data?.automation?.header_name, value: raw });
        } else {
          toast.success('Automation created');
          onOpenChange(false);
        }
        onSaved?.();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!submitting) onOpenChange(v); }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="automation-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Automation' : 'New Automation'}</DialogTitle>
          <DialogDescription>
            Register an external integration. Credentials are stored encrypted at rest.
          </DialogDescription>
        </DialogHeader>

        {createdValue ? (
          <div className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-300 rounded p-4">
              <div className="font-semibold text-emerald-900 mb-2">Automation created!</div>
              <div className="text-sm text-emerald-900 mb-3">
                Copy this credential NOW. It will be masked the next time you view this page.
              </div>
              {createdValue.header_name && (
                <div className="text-xs text-emerald-900">
                  Header name: <code className="bg-white px-2 py-0.5 rounded">{createdValue.header_name}</code>
                </div>
              )}
              <div className="mt-2 flex items-center gap-2">
                <code className="flex-1 bg-white px-2 py-1 rounded border text-xs break-all" data-testid="automation-created-value">
                  {createdValue.value}
                </code>
                <Button size="sm" onClick={() => { navigator.clipboard.writeText(createdValue.value); toast.success('Copied'); }} data-testid="automation-created-copy">
                  <Copy className="h-3.5 w-3.5 mr-1" />Copy
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)} data-testid="automation-created-done">Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="aut-name">Name *</Label>
                <Input id="aut-name" value={form.name} disabled={submitting}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. n8n Revenue Sync" data-testid="automation-name" />
              </div>
              <div>
                <Label htmlFor="aut-type">Type *</Label>
                <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                  <SelectTrigger id="aut-type" data-testid="automation-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(options.automation_types || []).map((t) => (
                      <SelectItem key={t} value={t}>{_typeLabel(t)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="aut-desc">Description</Label>
              <Textarea id="aut-desc" rows={2} value={form.description} disabled={submitting}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Free-form notes about what this integration does" data-testid="automation-description" />
            </div>

            <div className="border-t pt-3">
              <div className="font-semibold text-sm mb-2 text-slate-700">Inbound (external → us)</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="aut-hdr-name">Header Name</Label>
                  <Input id="aut-hdr-name" value={form.header_name} disabled={submitting}
                    onChange={(e) => setForm((f) => ({ ...f, header_name: e.target.value }))}
                    placeholder="X-API-Key" data-testid="automation-header-name" />
                </div>
                <div>
                  <Label htmlFor="aut-hdr-val">Header Value (leave empty to auto-generate)</Label>
                  <Input id="aut-hdr-val" type="password" value={form.header_value} disabled={submitting}
                    onChange={(e) => setForm((f) => ({ ...f, header_value: e.target.value }))}
                    placeholder={isEdit ? '(unchanged — use Rotate to regenerate)' : 'Auto-generated if blank'}
                    data-testid="automation-header-value" />
                </div>
              </div>
            </div>

            <div className="border-t pt-3">
              <div className="font-semibold text-sm mb-2 text-slate-700">Outbound (we → external)</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="aut-base-url">Base URL</Label>
                  <Input id="aut-base-url" value={form.base_url} disabled={submitting}
                    onChange={(e) => setForm((f) => ({ ...f, base_url: e.target.value }))}
                    placeholder="https://n8n.example.com/webhook/..." data-testid="automation-base-url" />
                </div>
                <div>
                  <Label htmlFor="aut-auth-method">Auth Method</Label>
                  <Select value={form.auth_method || ''} onValueChange={(v) => setForm((f) => ({ ...f, auth_method: v }))}>
                    <SelectTrigger id="aut-auth-method" data-testid="automation-auth-method"><SelectValue placeholder="select..." /></SelectTrigger>
                    <SelectContent>
                      {(options.auth_methods || []).map((m) => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-3">
                <Label htmlFor="aut-out-token">Outbound Token</Label>
                <Input id="aut-out-token" type="password" value={form.outbound_token} disabled={submitting}
                  onChange={(e) => setForm((f) => ({ ...f, outbound_token: e.target.value }))}
                  placeholder={isEdit ? '(unchanged — use Rotate to regenerate)' : 'Optional'}
                  data-testid="automation-outbound-token" />
              </div>
            </div>

            <div className="border-t pt-3">
              <div className="font-semibold text-sm mb-2 text-slate-700">
                Allowed Endpoints
                <span className="font-normal text-xs text-slate-500 ml-2">
                  (empty = all routes; checking endpoints restricts this key to ONLY those paths)
                </span>
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto bg-slate-50 rounded p-2 border">
                {(options.known_endpoints || []).map((ep) => (
                  <label key={ep.path} className="flex items-start gap-2 text-sm cursor-pointer hover:bg-white px-2 py-1 rounded">
                    <Checkbox
                      checked={form.allowed_endpoints.includes(ep.path)}
                      onCheckedChange={() => toggleEndpoint(ep.path)}
                      data-testid={`automation-endpoint-${ep.path.replace(/[^a-zA-Z0-9]/g, '-')}`}
                    />
                    <span>
                      <code className="text-xs font-mono">{ep.path}</code>
                      <div className="text-xs text-slate-500">{ep.group} · {ep.description}</div>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {!createdValue && (
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="automation-cancel">Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting} data-testid="automation-save">
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isEdit ? 'Save Changes' : 'Create'}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AutomationsTab;
